import axios from "axios";
import { parseMoney } from "../utils/money";

const API_URL = process.env.REACT_APP_API_URL;

const MONEY_FIELDS = new Set([
  "affordable_data_balance",
  "airtime_data_balance",
  "commission_rate",
  "amount",
  "price",
  "total_credited",
  "total_credit",
  "commission_amount",
  "min_value",
  "max_value",
]);

const normalizeMoneyFields = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeMoneyFields(item));
  }

  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const normalized = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (MONEY_FIELDS.has(key)) {
      normalized[key] = parseMoney(value);
    } else {
      normalized[key] = normalizeMoneyFields(value);
    }
  });

  return normalized;
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    if (response?.data) {
      response.data = normalizeMoneyFields(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If token expired, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          const response = await axios.post(
            `${API_URL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
                "Content-Type": "application/json",
              },
            },
          );

          const { access_token } = response.data.data;
          localStorage.setItem("access_token", access_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
